import React, { useContext, useEffect } from 'react';
import { Headphones, Pause, Play, SkipBack, SkipForward, Volume2, X } from 'lucide-react';
import { AppContext } from '../../Context';
import './index.scss';

const formatTime = (time) => {
  if (!Number.isFinite(time) || time <= 0) return '0:00';

  const minutes = Math.floor(time / 60);
  const seconds = Math.floor(time % 60);
  return `${minutes}:${seconds.toString().padStart(2, '0')}`;
};

const formatTrackName = (name = '') => (
  name
    .replace(/\.[^/.]+$/, '')
    .replace(/[-_]+/g, ' ')
    .trim() || 'Estimulación neuronal'
);

const NeuronalPlayerBar = () => {
  const {
    currentUser,
    currentAudio,
    currentAudioIndex,
    isPlaying,
    isAudioPlayerActive,
    currentAudioTime,
    audioDuration,
    playNext,
    playPrev,
    togglePlayPause,
    closeAudioPlayer,
    seekAudio,
    audioRef,
    setCurrentAudioTime,
    setAudioDuration,
  } = useContext(AppContext);

  const isStudent = Boolean(currentUser && currentUser.isMaster !== true && currentUser.teacher !== true);
  const shouldMountAudio = isStudent && Boolean(currentAudio);
  const shouldShowBar = shouldMountAudio && isAudioPlayerActive;
  const duration = Math.max(audioDuration || 0, 1);
  const currentTime = Math.min(currentAudioTime || 0, duration);
  const progressPercent = Math.min((currentTime / duration) * 100, 100);

  useEffect(() => {
    const audio = audioRef.current;

    if (!audio || !shouldMountAudio) return;

    if (isPlaying && isAudioPlayerActive) {
      const playPromise = audio.play();

      if (playPromise?.catch) {
        playPromise.catch((error) => {
          console.warn('No se pudo iniciar el audio de estimulación neuronal:', error);
        });
      }
    } else {
      audio.pause();
    }
  }, [audioRef, currentAudioIndex, currentAudio?.url, isAudioPlayerActive, isPlaying, shouldMountAudio]);

  const handleLoadedMetadata = () => {
    const nextDuration = audioRef.current?.duration;
    setAudioDuration(Number.isFinite(nextDuration) ? nextDuration : 0);
  };

  const handleTimeUpdate = () => {
    setCurrentAudioTime(audioRef.current?.currentTime || 0);
  };

  if (!shouldMountAudio) return null;

  return (
    <>
      <audio
        ref={audioRef}
        src={currentAudio.url}
        preload="metadata"
        onLoadedMetadata={handleLoadedMetadata}
        onTimeUpdate={handleTimeUpdate}
        onEnded={playNext}
      />

      {shouldShowBar && (
        <aside className="neural-player-bar" aria-label="Reproductor de estimulación neuronal">
          <div className="neural-player-bar__track">
            <span className="neural-player-bar__cover" aria-hidden="true">
              <Headphones size={22} />
            </span>
            <div className="neural-player-bar__meta">
              <span className="neural-player-bar__eyebrow">Estimulación neuronal</span>
              <strong>{formatTrackName(currentAudio.name)}</strong>
            </div>
          </div>

          <div className="neural-player-bar__controls" aria-label="Controles de reproducción">
            <button type="button" onClick={playPrev} aria-label="Pista anterior" title="Pista anterior">
              <SkipBack size={20} />
            </button>
            <button
              type="button"
              className="neural-player-bar__play"
              onClick={togglePlayPause}
              aria-label={isPlaying ? 'Pausar estimulación neuronal' : 'Reproducir estimulación neuronal'}
              title={isPlaying ? 'Pausar' : 'Reproducir'}
            >
              {isPlaying ? <Pause size={22} /> : <Play size={22} />}
            </button>
            <button type="button" onClick={playNext} aria-label="Siguiente pista" title="Siguiente pista">
              <SkipForward size={20} />
            </button>
          </div>

          <div className="neural-player-bar__timeline">
            <span>{formatTime(currentTime)}</span>
            <input
              type="range"
              min="0"
              max={duration}
              step="1"
              value={currentTime}
              onChange={(event) => seekAudio(event.target.value)}
              aria-label="Progreso de la pista"
              style={{ '--player-progress': `${progressPercent}%` }}
            />
            <span>{formatTime(audioDuration)}</span>
          </div>

          <div className="neural-player-bar__status" aria-hidden="true">
            <Volume2 size={18} />
          </div>

          <button
            type="button"
            className="neural-player-bar__close"
            onClick={closeAudioPlayer}
            aria-label="Cerrar reproductor"
            title="Cerrar reproductor"
          >
            <X size={18} />
          </button>
        </aside>
      )}
    </>
  );
};

export default NeuronalPlayerBar;
